/**
 * WizardProgress Component
 *
 * Displays the wizard step progress indicator.
 */

interface WizardProgressProps {
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (step: number) => void;
}

interface Step {
  id: number;
  label: string;
}

const STEPS: Step[] = [
  { id: 1, label: 'Details' },
  { id: 2, label: 'Source' },
  { id: 3, label: 'Content' },
  { id: 4, label: 'AI Magic' },
  { id: 5, label: 'Review' },
];

export function WizardProgress({ currentStep, completedSteps, onStepClick }: WizardProgressProps) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        padding: '20px 24px',
        background: 'white',
        borderRadius: 16,
        border: '1px solid var(--slate-200)',
      }}
    >
      {STEPS.map((step, index) => {
        const isCompleted = completedSteps.has(step.id);
        const isCurrent = currentStep === step.id;
        const isClickable = isCompleted || step.id === currentStep;

        return (
          <div key={step.id} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <button
              type="button"
              onClick={() => isClickable && onStepClick(step.id)}
              disabled={!isClickable}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: 'none',
                border: 'none',
                cursor: isClickable ? 'pointer' : 'default',
                padding: '10px 14px',
                borderRadius: 12,
                transition: 'background 0.15s',
                width: '100%',
              }}
              onMouseOver={(e) => {
                if (isClickable) {
                  e.currentTarget.style.background = 'var(--slate-50)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'none';
              }}
            >
              {/* Step number/checkmark */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 600,
                  flexShrink: 0,
                  background: isCompleted
                    ? 'var(--emerald-500)'
                    : isCurrent
                      ? 'var(--sky-500)'
                      : 'var(--slate-100)',
                  color: isCompleted || isCurrent ? '#fff' : 'var(--slate-400)',
                  boxShadow: isCompleted
                    ? '0 4px 6px -1px rgb(16 185 129 / 0.2)'
                    : isCurrent
                      ? '0 4px 6px -1px rgb(14 165 233 / 0.2)'
                      : 'none',
                }}
              >
                {isCompleted ? (
                  <iconify-icon icon="lucide:check" width="16" height="16"></iconify-icon>
                ) : (
                  step.id
                )}
              </div>

              {/* Label */}
              <span
                style={{
                  fontSize: 14,
                  fontWeight: isCurrent ? 600 : 500,
                  color: isCurrent ? 'var(--slate-900)' : isCompleted ? 'var(--slate-700)' : 'var(--slate-400)',
                  whiteSpace: 'nowrap',
                }}
              >
                {step.label}
              </span>
            </button>

            {/* Connector line */}
            {index < STEPS.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: isCompleted ? 'var(--emerald-500)' : 'var(--slate-200)',
                  marginLeft: 4,
                  minWidth: 20,
                  borderRadius: 1,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
